import React, { useEffect, useMemo, useState } from "react";
import { Header, Divider } from "semantic-ui-react";
import {
  Table,
  Tag,
  Input,
  Select,
  Space,
  message,
  Avatar,
  Button,
  Tooltip,
} from "antd";
import { SearchOutlined, EyeOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../hooks";
import { partnerDashboardApi } from "../../../../api/partnerDashboardAPI";
import dayjs from "dayjs";
import "./Page_PartnerDashboard.scss";

export function Page_PartnerDashboard() {
  const { accessToken, user } = useAuth();
  const { t } = useTranslation(["dashboard", "plans"]);
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);

  // Filtros
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(null);

  // Recuperamos el partnerId
  const partnerId = user?.user?._id || user?._id || user?.uid;

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      if (!partnerId) return;

      try {
        setLoading(true);
        const res = await partnerDashboardApi.getInstitutionsList(
          accessToken,
          partnerId,
        );

        if (mounted && res.items) {
          setItems(res.items);
        }
      } catch (e) {
        console.error(e);
        if (mounted) {
          message.error(t("partner.errorLoad"));
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    if (partnerId) {
      load();
    } else {
      setLoading(false);
    }

    return () => {
      mounted = false;
    };
  }, [accessToken, partnerId, t]);

  // Lógica de filtrado
  const filteredData = useMemo(() => {
    return items.filter((item) => {
      const matchesSearch =
        !search ||
        (item.nombre || "").toLowerCase().includes(search.toLowerCase()) ||
        (item.email || "").toLowerCase().includes(search.toLowerCase());

      const isExpired = item.expired_at
        ? dayjs().isAfter(dayjs(item.expired_at))
        : false;
      const isActive = item.active && !isExpired;

      const matchesStatus =
        !statusFilter ||
        (statusFilter === "active" && isActive) ||
        (statusFilter === "inactive" && !isActive);

      return matchesSearch && matchesStatus;
    });
  }, [items, search, statusFilter]);

  const columns = [
    {
      title: t("partner.colInstitution"),
      dataIndex: "nombre",
      key: "nombre",
      render: (text, record) => (
        <Space>
          <Avatar src={record.avatar} style={{ backgroundColor: "#1890ff" }}>
            {text ? text.charAt(0).toUpperCase() : "I"}
          </Avatar>
          <div>
            <div style={{ fontWeight: 600 }}>{text || t("partner.noName")}</div>
            <div style={{ fontSize: "12px", color: "#888" }}>
              {record.email}
            </div>
          </div>
        </Space>
      ),
    },
    {
      title: t("partner.colPlan"),
      dataIndex: ["planEduca", "title"],
      key: "plan",
      render: (plan) =>
        plan ? <Tag color="blue">{plan}</Tag> : <Tag>{t("plans:noPlan")}</Tag>,
    },
    {
      title: t("partner.colUsers"),
      key: "users",
      render: (_, r) => (
        <Space orientation="vertical" size={0} style={{ fontSize: "12px" }}>
          <span>
            {t("partner.teachers") + ":"} <strong>{r.profesores}</strong>
          </span>
          <span>
            {t("partner.students") + ":"} <strong>{r.alumnos}</strong>
          </span>
        </Space>
      ),
    },
    {
      title: t("partner.colStatus"),
      key: "status",
      render: (_, r) => {
        const isExpired = r.expired_at
          ? dayjs().isAfter(dayjs(r.expired_at))
          : false;
        const isActive = r.active && !isExpired;
        return isActive ? (
          <Tag color="green">{t("partner.statusActive")}</Tag>
        ) : (
          <Tag color="red">{t("partner.statusInactive")}</Tag>
        );
      },
    },
    {
      title: t("partner.colJoinDate"),
      dataIndex: "created_at",
      key: "created_at",
      render: (d) => (d ? dayjs(d).format("DD/MM/YYYY") : "-"),
    },
    {
      title: t("partner.colActions"),
      key: "actions",
      render: (_, r) => (
        <Tooltip title={t("partner.viewDetail")}>
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() =>
              (window.location.hash = `/partner/institutions/${r._id}`)
            }
          />
        </Tooltip>
      ),
    },
  ];

  return (
    // <--- CAMBIO AQUÍ: Usamos la clase unificada dashboard-partner-page
    <div className="dashboard-partner-page">
      <Header as="h2">{t("partner.title")}</Header>
      <Divider />

      {/* Barra de herramientas */}
      <div
        style={{ marginBottom: 20, display: "flex", gap: 10, flexWrap: "wrap" }}
      >
        <Input
          placeholder={t("partner.searchPlaceholder")}
          prefix={<SearchOutlined />}
          style={{ width: 300 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Select
          placeholder={t("partner.statusFilter")}
          style={{ width: 150 }}
          allowClear
          onChange={setStatusFilter}
          options={[
            { value: "active", label: t("partner.filterActive") },
            { value: "inactive", label: t("partner.filterInactive") },
          ]}
        />
      </div>

      <Table
        columns={columns}
        dataSource={filteredData}
        rowKey="_id"
        loading={loading}
        pagination={{ pageSize: 10 }}
        rowClassName="editable-row"
      />
    </div>
  );
}
